<template>
  <div>
    <!-- Filtros -->
    <b-row>
      <b-col cols="12">
        <b-card class="mb-3">
          <b-card-body>
            <!-- Linha 1: Filtro por Pessoa Jurídica -->
            <b-row v-if="isAdmin">
              <b-col cols="12" md="8" lg="6">
                <select-suggestion
                  label="Filtrar por Pessoa Jurídica"
                  column="company_name,trading_name,cnpj"
                  model="LegalEntity"
                  v-model="selectedLegalEntity"
                  :appendClearOption="true"
                  placeholderText="Digite o nome ou cnpj da pessoa jurídica"
                  :itemTextFormat="legalEntityItemTextFormat"
                  @selected-model="onLegalEntitySelected"
                />
              </b-col>
            </b-row>

            <!-- Linha 2: Filtro por Período (Datas) -->
            <b-row class="mt-2">
              <b-col cols="12" md="6" lg="3">
                <b-form-group label="Data Inicial">
                  <b-form-input
                    type="date"
                    v-model="dateStart"
                    @change="onPeriodChanged"
                  />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6" lg="3">
                <b-form-group label="Data Final">
                  <b-form-input
                    type="date"
                    v-model="dateEnd"
                    @change="onPeriodChanged"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Linha 3: Filtros Rápidos -->
            <b-row class="mt-2">
              <b-col cols="12">
                <small class="text-muted d-block mb-2">Períodos Rápidos:</small>
                <div class="d-flex flex-wrap" style="gap: 0.5rem;">
                  <b-button
                    size="sm"
                    :variant="
                      selectedPeriod === 'today'
                        ? 'primary'
                        : 'outline-secondary'
                    "
                    @click="setPeriod('today')"
                  >
                    Hoje
                  </b-button>
                  <b-button
                    size="sm"
                    :variant="
                      selectedPeriod === 'yesterday'
                        ? 'primary'
                        : 'outline-secondary'
                    "
                    @click="setPeriod('yesterday')"
                  >
                    Ontem
                  </b-button>
                  <b-button
                    size="sm"
                    :variant="
                      selectedPeriod === '7days'
                        ? 'primary'
                        : 'outline-secondary'
                    "
                    @click="setPeriod('7days')"
                  >
                    Últimos 7 dias
                  </b-button>
                  <b-button
                    size="sm"
                    :variant="
                      selectedPeriod === '15days'
                        ? 'primary'
                        : 'outline-secondary'
                    "
                    @click="setPeriod('15days')"
                  >
                    Últimos 15 dias
                  </b-button>
                  <b-button
                    size="sm"
                    :variant="
                      selectedPeriod === '30days'
                        ? 'primary'
                        : 'outline-secondary'
                    "
                    @click="setPeriod('30days')"
                  >
                    Últimos 30 dias
                  </b-button>
                  <b-button
                    size="sm"
                    :variant="
                      selectedPeriod === 'thisMonth'
                        ? 'primary'
                        : 'outline-secondary'
                    "
                    @click="setPeriod('thisMonth')"
                  >
                    Este mês
                  </b-button>
                  <b-button
                    size="sm"
                    :variant="
                      selectedPeriod === 'lastMonth'
                        ? 'primary'
                        : 'outline-secondary'
                    "
                    @click="setPeriod('lastMonth')"
                  >
                    Mês passado
                  </b-button>
                </div>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Indicadores em Abas -->
    <b-card>
      <b-tabs content-class="mt-3" v-model="activeTab">
        <!-- Aba: Nível de Criticidade -->
        <b-tab title="Nível de Criticidade" active>
          <b-row>
            <b-col cols="12" lg="8">
              <b-card>
                <b-card-title>Por Nível de Criticidade</b-card-title>
                <b-card-body
                  v-if="severityIndicators && severityIndicators.length > 0"
                >
                  <vue-apex-charts
                    type="donut"
                    height="350"
                    :options="chartOptions"
                    :series="chartSeries"
                  />
                </b-card-body>
                <b-alert v-else show variant="info" class="m-3">
                  Não há dados para exibir
                </b-alert>
              </b-card>
            </b-col>
            <b-col cols="12" lg="4" v-if="totalFormularios > 0">
              <b-card>
                <b-card-body>
                  <div class="text-center mb-3">
                    <h4 class="mb-1">Total de Formulários</h4>
                    <h2 class="text-primary">{{ totalFormularios }}</h2>
                  </div>
                  <hr />
                  <div
                    v-if="severityIndicators && severityIndicators.length > 0"
                  >
                    <div
                      v-for="(indicator, index) in severityIndicators"
                      :key="index"
                      class="d-flex justify-content-between align-items-center mb-3"
                    >
                      <div class="d-flex align-items-center">
                        <b-badge
                          :style="{
                            backgroundColor: getColorForBadge(
                              indicator.severity.color
                            )
                          }"
                          class="mr-2"
                          style="width: 20px; height: 20px; border-radius: 4px"
                        ></b-badge>
                        <span>{{ indicator.severity.label }}</span>
                      </div>
                      <strong class="text-primary">{{
                        indicator.total
                      }}</strong>
                    </div>
                  </div>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>
        </b-tab>

        <!-- Aba: Tipo de Relato -->
        <b-tab title="Tipo de Relato">
          <b-row>
            <b-col cols="12" lg="8">
              <b-card>
                <b-card-title>Por Tipo de Relato</b-card-title>
                <b-card-body
                  v-if="
                    typeReportingIndicators &&
                      typeReportingIndicators.length > 0
                  "
                >
                  <vue-apex-charts
                    type="donut"
                    height="350"
                    :options="typeReportingChartOptions"
                    :series="typeReportingChartSeries"
                  />
                </b-card-body>
                <b-alert v-else show variant="info" class="m-3">
                  Não há dados para exibir
                </b-alert>
              </b-card>
            </b-col>
            <b-col cols="12" lg="4" v-if="totalTypeReporting > 0">
              <b-card>
                <b-card-body>
                  <div class="text-center mb-3">
                    <h4 class="mb-1">Total por Tipo</h4>
                    <h2 class="text-primary">{{ totalTypeReporting }}</h2>
                  </div>
                  <hr />
                  <div
                    v-if="
                      typeReportingIndicators &&
                        typeReportingIndicators.length > 0
                    "
                  >
                    <div
                      v-for="(indicator, index) in typeReportingIndicators"
                      :key="index"
                      class="d-flex justify-content-between align-items-start mb-3"
                    >
                      <div
                        class="d-flex align-items-start flex-grow-1"
                        style="min-width: 0;"
                      >
                        <b-badge
                          :style="{
                            backgroundColor: getColorForTypeReporting(
                              indicator.type_reporting.id
                            )
                          }"
                          class="mr-2 flex-shrink-0"
                          style="width: 20px; height: 20px; border-radius: 4px; margin-top: 2px;"
                        ></b-badge>
                        <span
                          class="text-break"
                          style="word-wrap: break-word; overflow-wrap: break-word;"
                          >{{ indicator.type_reporting.name }}</span
                        >
                      </div>
                      <strong class="text-primary flex-shrink-0 ml-2">{{
                        indicator.total
                      }}</strong>
                    </div>
                  </div>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>
        </b-tab>

        <!-- Aba: Situação do Tratamento -->
        <b-tab title="Situação do Tratamento">
          <b-row>
            <b-col cols="12" lg="8">
              <b-card>
                <b-card-title>Por Situação do Tratamento</b-card-title>
                <b-card-body
                  v-if="statusIndicators && statusIndicators.length > 0"
                >
                  <vue-apex-charts
                    type="donut"
                    height="350"
                    :options="statusChartOptions"
                    :series="statusChartSeries"
                  />
                </b-card-body>
                <b-alert v-else show variant="info" class="m-3">
                  Não há dados para exibir
                </b-alert>
              </b-card>
            </b-col>
            <b-col cols="12" lg="4" v-if="totalStatus > 0">
              <b-card>
                <b-card-body>
                  <div class="text-center mb-3">
                    <h4 class="mb-1">Total por Situação</h4>
                    <h2 class="text-primary">{{ totalStatus }}</h2>
                  </div>
                  <hr />
                  <div v-if="statusIndicators && statusIndicators.length > 0">
                    <div
                      v-for="(indicator, index) in statusIndicators"
                      :key="index"
                      class="d-flex justify-content-between align-items-center mb-3"
                    >
                      <div class="d-flex align-items-center">
                        <HandlingStatusBadge
                          class="mr-2"
                          :color="indicator.status.color"
                          :label="indicator.status.label"
                        />
                      </div>
                      <strong class="text-primary">{{
                        indicator.total
                      }}</strong>
                    </div>
                  </div>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>
        </b-tab>

        <!-- Aba: Identificação -->
        <b-tab title="Identificação">
          <b-row>
            <b-col cols="12" lg="8">
              <b-card>
                <b-card-title>Por Identificação</b-card-title>
                <b-card-body
                  v-if="
                    identificationIndicators &&
                      identificationIndicators.length > 0
                  "
                >
                  <vue-apex-charts
                    type="donut"
                    height="350"
                    :options="identificationChartOptions"
                    :series="identificationChartSeries"
                  />
                </b-card-body>
                <b-alert v-else show variant="info" class="m-3">
                  Não há dados para exibir
                </b-alert>
              </b-card>
            </b-col>
            <b-col cols="12" lg="4" v-if="totalIdentification > 0">
              <b-card>
                <b-card-body>
                  <div class="text-center mb-3">
                    <h4 class="mb-1">Total por Identificação</h4>
                    <h2 class="text-primary">{{ totalIdentification }}</h2>
                  </div>
                  <hr />
                  <div
                    v-if="
                      identificationIndicators &&
                        identificationIndicators.length > 0
                    "
                  >
                    <div
                      v-for="(indicator, index) in identificationIndicators"
                      :key="index"
                      class="d-flex justify-content-between align-items-center mb-3"
                    >
                      <div class="d-flex align-items-center">
                        <b-badge
                          :style="{
                            backgroundColor: indicator.is_identify
                              ? '#28C76F'
                              : '#82868B'
                          }"
                          class="mr-2"
                          style="width: 20px; height: 20px; border-radius: 4px"
                        ></b-badge>
                        <span>{{ indicator.identification_status }}</span>
                      </div>
                      <strong class="text-primary">{{
                        indicator.total
                      }}</strong>
                    </div>
                  </div>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>
        </b-tab>
      </b-tabs>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardTitle,
  BCardSubTitle,
  BCardBody,
  BRow,
  BCol,
  BAlert,
  BBadge,
  BFormGroup,
  BFormInput,
  BButton,
  BTabs,
  BTab
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import { $themeColors } from '@themeConfig'
import ReportingChannelFormHandlingService from '@/services/api/reporting-channel/ReportingChannelFormHandlingService'
import SelectSuggestion from '@/components/SelectSuggestion.vue'
import HandlingStatusBadge from '@/components/HandlingStatusBadge.vue'
import { isAdmin } from '@/util/Util'

export default {
  components: {
    BCard,
    BCardTitle,
    BCardSubTitle,
    BCardBody,
    BRow,
    BCol,
    BAlert,
    BBadge,
    BFormGroup,
    BFormInput,
    BButton,
    BTabs,
    BTab,
    VueApexCharts,
    SelectSuggestion,
    HandlingStatusBadge
  },
  data: () => {
    // Calcular datas padrão (últimos 15 dias)
    const endDate = new Date()
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - 15)

    return {
      service: null,
      dashboardData: null,
      selectedLegalEntity: null,
      selectedPeriod: '15days',
      dateStart: startDate.toISOString().split('T')[0],
      dateEnd: endDate.toISOString().split('T')[0],
      activeTab: 0
    }
  },
  computed: {
    isAdmin() {
      return isAdmin()
    },
    // Processar indicadores de criticidade para o gráfico
    severityIndicators() {
      if (!this.dashboardData || !this.dashboardData.indicators) {
        return []
      }
      // Retornar todos os indicadores (incluindo "Não classificado" que tem key: null)
      // O severity existe mesmo quando key é null (para "Não classificado")
      return this.dashboardData.indicators.filter(
        indicator => indicator.severity && indicator.total > 0
      )
    },
    chartSeries() {
      if (!this.severityIndicators || this.severityIndicators.length === 0) {
        return []
      }
      return this.severityIndicators.map(indicator => {
        const total = indicator.total
        return typeof total === 'number' && !isNaN(total) ? total : 0
      })
    },
    chartLabels() {
      if (!this.severityIndicators || this.severityIndicators.length === 0) {
        return []
      }
      return this.severityIndicators.map(indicator => {
        return (indicator.severity && indicator.severity.label) || 'Sem label'
      })
    },
    chartColors() {
      if (!this.severityIndicators || this.severityIndicators.length === 0) {
        return []
      }
      return this.severityIndicators.map(indicator => {
        const colorName = indicator.severity && indicator.severity.color
        // Mapear cores do enum para valores hexadecimais do tema
        if (
          colorName &&
          typeof colorName === 'string' &&
          colorName.startsWith('#')
        ) {
          return colorName
        }
        // Usar o mesmo método de mapeamento usado nos badges
        const color = this.getColorForBadge(colorName)
        return color || '#5E50EE' // fallback para uma cor padrão
      })
    },
    totalFormularios() {
      if (!this.severityIndicators || this.severityIndicators.length === 0) {
        return 0
      }
      return this.severityIndicators.reduce((sum, indicator) => {
        const total = indicator.total
        const numTotal = typeof total === 'number' && !isNaN(total) ? total : 0
        return sum + numTotal
      }, 0)
    },
    // Processar indicadores por tipo de relato
    typeReportingIndicators() {
      if (
        !this.dashboardData ||
        !this.dashboardData.type_reporting_indicators
      ) {
        return []
      }
      return this.dashboardData.type_reporting_indicators.filter(
        indicator => indicator.type_reporting && indicator.total > 0
      )
    },
    typeReportingChartSeries() {
      if (
        !this.typeReportingIndicators ||
        this.typeReportingIndicators.length === 0
      ) {
        return []
      }
      return this.typeReportingIndicators.map(indicator => indicator.total)
    },
    typeReportingChartLabels() {
      if (
        !this.typeReportingIndicators ||
        this.typeReportingIndicators.length === 0
      ) {
        return []
      }
      return this.typeReportingIndicators.map(
        indicator =>
          (indicator.type_reporting && indicator.type_reporting.name) ||
          'Sem nome'
      )
    },
    typeReportingChartColors() {
      if (
        !this.typeReportingIndicators ||
        this.typeReportingIndicators.length === 0
      ) {
        return []
      }
      // Gerar cores fixas para cada tipo de relato baseado no ID
      return this.typeReportingIndicators.map(indicator => {
        return this.getColorForTypeReporting(indicator.type_reporting.id)
      })
    },
    totalTypeReporting() {
      if (
        !this.typeReportingIndicators ||
        this.typeReportingIndicators.length === 0
      ) {
        return 0
      }
      return this.typeReportingIndicators.reduce((sum, indicator) => {
        const total = indicator.total
        const numTotal = typeof total === 'number' && !isNaN(total) ? total : 0
        return sum + numTotal
      }, 0)
    },
    // Processar indicadores por situação do tratamento
    statusIndicators() {
      if (!this.dashboardData || !this.dashboardData.status_indicators) {
        return []
      }
      return this.dashboardData.status_indicators
        .filter(indicator => indicator.status && indicator.total > 0)
        .map(indicator => {
          const color =
            (indicator.status && indicator.status.color) || '#5E50EE' // fallback para OPEN em azul/roxo

          return {
            ...indicator,
            status: {
              ...indicator.status,
              color
            },
            total_time_formatted: indicator.total_time_formatted || null
          }
        })
    },
    statusChartSeries() {
      if (!this.statusIndicators || this.statusIndicators.length === 0) {
        return []
      }
      return this.statusIndicators.map(indicator => {
        const total = indicator.total
        return typeof total === 'number' && !isNaN(total) ? total : 0
      })
    },
    statusChartLabels() {
      if (!this.statusIndicators || this.statusIndicators.length === 0) {
        return []
      }
      return this.statusIndicators.map(indicator => {
        return (indicator.status && indicator.status.label) || 'Sem label'
      })
    },
    statusChartColors() {
      if (!this.statusIndicators || this.statusIndicators.length === 0) {
        return []
      }
      return this.statusIndicators.map(indicator => {
        const colorName =
          (indicator.status && indicator.status.color) || '#5E50EE'
        // Mapear cores do enum para valores hexadecimais do tema
        if (
          colorName &&
          typeof colorName === 'string' &&
          colorName.startsWith('#')
        ) {
          return colorName
        }
        // Mapear 'grey' para 'secondary' se não existir no tema
        const mappedColorName = colorName === 'grey' ? 'secondary' : colorName
        const color = this.getColorForBadge(mappedColorName)
        return color || '#5E50EE' // fallback para uma cor padrão
      })
    },
    totalStatus() {
      if (!this.statusIndicators || this.statusIndicators.length === 0) {
        return 0
      }
      return this.statusIndicators.reduce((sum, indicator) => {
        const total = indicator.total
        const numTotal = typeof total === 'number' && !isNaN(total) ? total : 0
        return sum + numTotal
      }, 0)
    },
    // Processar indicadores por identificação
    identificationIndicators() {
      if (
        !this.dashboardData ||
        !this.dashboardData.identification_indicators
      ) {
        return []
      }
      return this.dashboardData.identification_indicators.filter(
        indicator => indicator.total > 0
      )
    },
    identificationChartSeries() {
      if (
        !this.identificationIndicators ||
        this.identificationIndicators.length === 0
      ) {
        return []
      }
      return this.identificationIndicators.map(indicator => {
        const total = indicator.total
        return typeof total === 'number' && !isNaN(total) ? total : 0
      })
    },
    identificationChartLabels() {
      if (
        !this.identificationIndicators ||
        this.identificationIndicators.length === 0
      ) {
        return []
      }
      return this.identificationIndicators.map(indicator => {
        return indicator.identification_status || 'Sem status'
      })
    },
    identificationChartColors() {
      if (
        !this.identificationIndicators ||
        this.identificationIndicators.length === 0
      ) {
        return []
      }
      return this.identificationIndicators.map(indicator => {
        return indicator.is_identify ? '#28C76F' : '#82868B' // success para com identificação, secondary para sem
      })
    },
    totalIdentification() {
      if (
        !this.identificationIndicators ||
        this.identificationIndicators.length === 0
      ) {
        return 0
      }
      return this.identificationIndicators.reduce((sum, indicator) => {
        const total = indicator.total
        const numTotal = typeof total === 'number' && !isNaN(total) ? total : 0
        return sum + numTotal
      }, 0)
    },
    chartOptions() {
      if (!this.severityIndicators || this.severityIndicators.length === 0) {
        return {}
      }

      const self = this
      return {
        chart: {
          type: 'donut',
          toolbar: {
            show: false
          }
        },
        labels: this.chartLabels,
        colors: this.chartColors,
        legend: {
          show: true,
          position: 'bottom'
        },
        dataLabels: {
          enabled: true,
          formatter: function(val) {
            return val.toFixed(1) + '%'
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '70%',
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '16px',
                  fontWeight: 600
                },
                value: {
                  show: true,
                  fontSize: '14px'
                },
                total: {
                  show: true,
                  label: 'Total',
                  formatter: () => {
                    return self.totalFormularios
                  }
                }
              }
            }
          }
        },
        tooltip: {
          y: {
            formatter: function(value, { seriesIndex }) {
              // Obter o valor real da série usando os dados já calculados
              const series = self.chartSeries
              let numValue = 0
              if (series && series[seriesIndex] !== undefined) {
                numValue =
                  typeof series[seriesIndex] === 'number' &&
                  !isNaN(series[seriesIndex])
                    ? series[seriesIndex]
                    : 0
              }

              // Usar o total calculado
              const total = self.totalFormularios
              const numTotal =
                typeof total === 'number' && !isNaN(total) && total > 0
                  ? total
                  : 1

              // Calcular a porcentagem
              const percent = ((numValue / numTotal) * 100).toFixed(1)
              return numValue + ' (' + percent + '%)'
            }
          }
        }
      }
    },
    typeReportingChartOptions() {
      if (
        !this.typeReportingIndicators ||
        this.typeReportingIndicators.length === 0
      ) {
        return {}
      }

      const self = this
      return {
        chart: {
          type: 'donut',
          toolbar: {
            show: false
          }
        },
        labels: this.typeReportingChartLabels,
        colors: this.typeReportingChartColors,
        legend: {
          show: true,
          position: 'bottom'
        },
        dataLabels: {
          enabled: true,
          formatter: function(val) {
            return val.toFixed(1) + '%'
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '70%',
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '16px',
                  fontWeight: 600
                },
                value: {
                  show: true,
                  fontSize: '14px'
                },
                total: {
                  show: true,
                  label: 'Total',
                  formatter: () => {
                    return self.totalTypeReporting
                  }
                }
              }
            }
          }
        },
        tooltip: {
          y: {
            formatter: function(value, { seriesIndex }) {
              // Obter o valor real da série usando os dados já calculados
              const series = self.typeReportingChartSeries
              let numValue = 0
              if (series && series[seriesIndex] !== undefined) {
                numValue =
                  typeof series[seriesIndex] === 'number' &&
                  !isNaN(series[seriesIndex])
                    ? series[seriesIndex]
                    : 0
              }

              // Usar o total calculado
              const total = self.totalTypeReporting
              const numTotal =
                typeof total === 'number' && !isNaN(total) && total > 0
                  ? total
                  : 1

              // Calcular a porcentagem
              const percent = ((numValue / numTotal) * 100).toFixed(1)
              return numValue + ' (' + percent + '%)'
            }
          }
        }
      }
    },
    statusChartOptions() {
      if (!this.statusIndicators || this.statusIndicators.length === 0) {
        return {}
      }

      const self = this
      return {
        chart: {
          type: 'donut',
          toolbar: {
            show: false
          }
        },
        labels: this.statusChartLabels,
        colors: this.statusChartColors,
        legend: {
          show: true,
          position: 'bottom'
        },
        dataLabels: {
          enabled: true,
          formatter: function(val) {
            return val.toFixed(1) + '%'
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '70%',
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '16px',
                  fontWeight: 600
                },
                value: {
                  show: true,
                  fontSize: '14px'
                },
                total: {
                  show: true,
                  label: 'Total',
                  formatter: () => {
                    return self.totalStatus
                  }
                }
              }
            }
          }
        },
        tooltip: {
          y: {
            formatter: function(value, { seriesIndex }) {
              // Obter o valor real da série usando os dados já calculados
              const series = self.statusChartSeries
              let numValue = 0
              if (series && series[seriesIndex] !== undefined) {
                numValue =
                  typeof series[seriesIndex] === 'number' &&
                  !isNaN(series[seriesIndex])
                    ? series[seriesIndex]
                    : 0
              }

              // Usar o total calculado
              const total = self.totalStatus
              const numTotal =
                typeof total === 'number' && !isNaN(total) && total > 0
                  ? total
                  : 1

              // Calcular a porcentagem
              const percent = ((numValue / numTotal) * 100).toFixed(1)
              return numValue + ' (' + percent + '%)'
            }
          }
        }
      }
    },
    identificationChartOptions() {
      if (
        !this.identificationIndicators ||
        this.identificationIndicators.length === 0
      ) {
        return {}
      }

      const self = this
      return {
        chart: {
          type: 'donut',
          toolbar: {
            show: false
          }
        },
        labels: this.identificationChartLabels,
        colors: this.identificationChartColors,
        legend: {
          show: true,
          position: 'bottom'
        },
        dataLabels: {
          enabled: true,
          formatter: function(val) {
            if (typeof val !== 'number' || isNaN(val)) {
              return '0.0%'
            }
            return val.toFixed(1) + '%'
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '70%',
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '16px',
                  fontWeight: 600
                },
                value: {
                  show: true,
                  fontSize: '14px'
                },
                total: {
                  show: true,
                  label: 'Total',
                  formatter: () => {
                    return self.totalIdentification
                  }
                }
              }
            }
          }
        },
        tooltip: {
          y: {
            formatter: function(value, { seriesIndex }) {
              // Obter o valor real da série usando os dados já calculados
              const series = self.identificationChartSeries
              let numValue = 0
              if (series && series[seriesIndex] !== undefined) {
                numValue =
                  typeof series[seriesIndex] === 'number' &&
                  !isNaN(series[seriesIndex])
                    ? series[seriesIndex]
                    : 0
              }

              // Usar o total calculado
              const total = self.totalIdentification
              const numTotal =
                typeof total === 'number' && !isNaN(total) && total > 0
                  ? total
                  : 1

              // Calcular a porcentagem
              const percent = ((numValue / numTotal) * 100).toFixed(1)
              return numValue + ' (' + percent + '%)'
            }
          }
        }
      }
    }
  },
  methods: {
    getStatusColor(indicator) {
      const colorName =
        (indicator &&
          indicator.status &&
          indicator.status.color &&
          indicator.status.color) ||
        '#5E50EE'

      return this.getColorForBadge(colorName)
    },
    getColorForBadge(colorName) {
      // Se já for um valor hexadecimal, retornar como está
      if (colorName && colorName.startsWith('#')) {
        return colorName
      }

      // Mapeamento explícito de cores do enum para cores do tema
      const colorMap = {
        primary: $themeColors.primary || '#7367F0',
        info: $themeColors.info || '#00CFE8',
        warning: $themeColors.warning || '#FF9F43',
        danger: $themeColors.danger || '#EA5455',
        success: $themeColors.success || '#28C76F',
        dark: $themeColors.dark || '#4B4B4B',
        secondary: $themeColors.secondary || '#82868B',
        grey: $themeColors.secondary || '#82868B'
      }

      // Retornar cor mapeada ou buscar no tema como fallback
      return colorMap[colorName] || $themeColors[colorName] || colorName
    },
    getColorForTypeReporting(typeId) {
      // Paleta de cores distintas para tipos de relato (15+ cores)
      const colorPalette = [
        '#7367F0', // primary
        '#28C76F', // success
        '#FF9F43', // warning
        '#EA5455', // danger
        '#00CFE8', // info
        '#82868B', // secondary
        '#9C27B0', // purple
        '#FF6B6B', // coral
        '#4ECDC4', // teal
        '#45B7D1', // sky blue
        '#FFA07A', // light salmon
        '#98D8C8', // mint
        '#F7DC6F', // yellow
        '#BB8FCE', // lavender
        '#85C1E2', // light blue
        '#F8B739', // orange
        '#52BE80', // green
        '#EC7063', // pink
        '#5DADE2', // blue
        '#F1948A' // light red
      ]

      // Gerar um índice baseado no ID do tipo para garantir cor fixa
      // Usar hash simples do ID para mapear para uma cor
      if (!typeId) {
        return colorPalette[0]
      }

      // Converter ID para número e usar módulo para obter índice
      let hash = 0
      const idString = String(typeId)
      for (let i = 0; i < idString.length; i++) {
        hash = (hash << 5) - hash + idString.charCodeAt(i)
        hash = hash & hash // Convert to 32bit integer
      }

      const index = Math.abs(hash) % colorPalette.length
      return colorPalette[index]
    },
    legalEntityItemTextFormat(model) {
      let name = `${model.company_name} - ${model.trading_name}`
      const hasCnpj = !!model.cnpj
      if (hasCnpj) {
        name = `${name} ( ${this.cnpjFormat(model.cnpj)}) `
      }
      return name
    },
    cnpjFormat(cnpj) {
      if (!cnpj) return ''
      const cleaned = cnpj.replace(/\D/g, '')
      if (cleaned.length !== 14) return cnpj
      return cleaned.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        '$1.$2.$3/$4-$5'
      )
    },
    onLegalEntitySelected(legalEntity) {
      // Quando selecionar ou limpar, recarregar os dados
      this.loadDashboardData()
    },
    setPeriod(period) {
      this.selectedPeriod = period
      const endDate = new Date()
      const startDate = new Date()

      switch (period) {
        case 'today':
          startDate.setHours(0, 0, 0, 0)
          endDate.setHours(23, 59, 59, 999)
          break
        case 'yesterday':
          startDate.setDate(startDate.getDate() - 1)
          startDate.setHours(0, 0, 0, 0)
          endDate.setDate(endDate.getDate() - 1)
          endDate.setHours(23, 59, 59, 999)
          break
        case '7days':
          startDate.setDate(startDate.getDate() - 7)
          startDate.setHours(0, 0, 0, 0)
          endDate.setHours(23, 59, 59, 999)
          break
        case '15days':
          startDate.setDate(startDate.getDate() - 15)
          startDate.setHours(0, 0, 0, 0)
          endDate.setHours(23, 59, 59, 999)
          break
        case '30days':
          startDate.setDate(startDate.getDate() - 30)
          startDate.setHours(0, 0, 0, 0)
          endDate.setHours(23, 59, 59, 999)
          break
        case 'thisMonth':
          startDate.setDate(1)
          startDate.setHours(0, 0, 0, 0)
          endDate.setHours(23, 59, 59, 999)
          break
        case 'lastMonth':
          // Primeiro dia do mês passado
          startDate.setMonth(startDate.getMonth() - 1)
          startDate.setDate(1)
          startDate.setHours(0, 0, 0, 0)
          // Último dia do mês passado
          endDate.setDate(0) // Isso retorna o último dia do mês anterior
          endDate.setHours(23, 59, 59, 999)
          break
      }

      this.dateStart = startDate.toISOString().split('T')[0]
      this.dateEnd = endDate.toISOString().split('T')[0]
      this.loadDashboardData()
    },
    onPeriodChanged() {
      // Quando as datas são alteradas manualmente, remover o período selecionado
      this.selectedPeriod = null
      this.loadDashboardData()
    },
    loadDashboardData() {
      this.$vs.loading()
      const params = {}
      if (this.selectedLegalEntity && this.selectedLegalEntity.id) {
        params.legal_entity_id = this.selectedLegalEntity.id
      }
      if (this.dateStart) {
        params.date_start = this.dateStart
      }
      if (this.dateEnd) {
        params.date_end = this.dateEnd
      }
      this.service
        .getDashboardData(params)
        .then(
          data => {
            this.dashboardData = data
            this.$vs.loading.close()
          },
          error => {
            this.$vs.loading.close()
            this.notifyError(
              this.$vs,
              'Não foi possível carregar os dados do dashboard'
            )
          }
        )
        .finally(() => {
          this.$vs.loading.close()
        })
    }
  },
  beforeMount() {
    this.service = ReportingChannelFormHandlingService.build(this.$vs)
  },
  mounted() {
    this.loadDashboardData()
  }
}
</script>

<style scoped>
/* Estilos para impressão - cada gráfico em uma página A4 separada */
@media print {
  /* Forçar quebra de página antes de cada gráfico (exceto o primeiro) */
  .print-page-break {
    page-break-before: always;
    break-before: page;
  }

  /* Garantir que cada gráfico não seja dividido entre páginas */
  .print-page-break .b-card {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  /* Ajustar margens para melhor aproveitamento do espaço A4 */
  @page {
    size: A4;
    margin: 1.5cm;
  }

  /* Ocultar elementos desnecessários na impressão */
  .b-alert {
    page-break-inside: avoid;
  }

  /* Garantir que os gráficos não sejam cortados */
  .b-card {
    page-break-inside: avoid;
    break-inside: avoid;
  }
}
</style>
